import { RoninSaleOrder } from '@apeiron/common'
import { ApproveHook } from '@apeiron/contract'
import { CurrencyTokenType } from '@apeiron/currency'
import { ChainType, useAccountContext } from '@apeiron/temp'
import usePolygonMarketplace from '@src/hooks/contract/usePolygonMarketplace'
import useRoninMarketplace from '@src/hooks/contract/useRoninMarketplace'
import { useCallback } from 'react'
import { Address } from 'viem'

const useMarketplace = (): Hook => {
  const { account } = useAccountContext()

  const {
    buy: roninBuy,
    cancelListing: roninCancelListing,
    createListing: roninCreateListing
  } = useRoninMarketplace()

  const {
    buy: polygonBuy,
    cancelListing: polygonCancelListing,
    createListing: polygonCreateListing,
    updateListing: polygonUpdateListing
  } = usePolygonMarketplace()

  const createListing = useCallback(
    async (params: MarketplaceSellParams): Promise<boolean> => {
      switch (account.chainType) {
        case ChainType.Polygon:
        case ChainType.PolygonTestnet:
          return polygonCreateListing(params)

        case ChainType.Ronin:
        case ChainType.RoninTestnet:
        default:
          return roninCreateListing(params)
      }
    },
    [account.chainType, polygonCreateListing, roninCreateListing]
  )

  const updateListing = useCallback(
    async (params: MarketplaceSellParams): Promise<boolean> => {
      switch (account.chainType) {
        case ChainType.Polygon:
        case ChainType.PolygonTestnet:
          return polygonUpdateListing(params)

        case ChainType.Ronin:
        case ChainType.RoninTestnet:
        default:
          return false
      }
    },
    [account.chainType, polygonUpdateListing]
  )

  const cancelListing = useCallback(
    async (params: MarketplaceCancelSellParams): Promise<boolean> => {
      switch (account.chainType) {
        case ChainType.Polygon:
        case ChainType.PolygonTestnet:
          return polygonCancelListing(params)

        case ChainType.Ronin:
        case ChainType.RoninTestnet:
        default:
          return roninCancelListing(params)
      }
    },
    [account.chainType, polygonCancelListing, roninCancelListing]
  )

  const buy = useCallback(
    async (params: MarketplaceBuyParams): Promise<boolean> => {
      switch (account.chainType) {
        case ChainType.Polygon:
        case ChainType.PolygonTestnet:
          return polygonBuy(params)

        case ChainType.Ronin:
        case ChainType.RoninTestnet:
        default:
          return roninBuy(params)
      }
    },
    [account.chainType, polygonBuy, roninBuy]
  )

  return { buy, cancelListing, createListing, updateListing }
}

type MarketplaceVerify = (nftId: string) => Promise<boolean>

export type MarketplaceBuyParams = {
  contractParams: {
    ownerAddress: string
    listAt: string // e.g. '2022-11-28T00:00:00.000Z'
    nftAddress: Address
    nftId: string
    price: number
    erc1155?: {
      listIndex: number
      quantity: number
      sellerAddress: string
    }
    roninSaleOrder?: RoninSaleOrder
    settleOrderTokenType: CurrencyTokenType
  }
  onApproved: () => void
  onBought: () => void
  onFailed: () => void
  verify: MarketplaceVerify
}

export type MarketplaceCancelSellParams = {
  contractParams: {
    nftAddress: string
    nftId: string
    erc1155?: {
      listIndex: number
    }
    roninSaleOrder?: RoninSaleOrder
  }
  verify?: MarketplaceVerify
}

export type MarketplaceSellParams = {
  contractParams: {
    duration: number
    endPrice: string
    erc1155?: ERC1155Params
    nftAddress: Address
    nftId: string
    startPrice: string
    tokenAddress: Address
  }
  approve: ApproveHook
  verify: MarketplaceVerify
}

type ERC1155Params = {
  index: number
  quantity: number
}

type Hook = {
  buy: (params: MarketplaceBuyParams) => Promise<boolean>
  cancelListing: (params: MarketplaceCancelSellParams) => Promise<boolean>
  createListing: (params: MarketplaceSellParams) => Promise<boolean>
  updateListing: (params: MarketplaceSellParams) => Promise<boolean>
}

export default useMarketplace
